import brightcovePlayerLoader from '@brightcove/player-loader';
import * as Sentry from '@sentry/browser';

import { UserStore } from 'Main/user-store';
import VideoCountdown from 'Main/video-countdown';
import { isTouchPrimary } from 'Utils/device';
import { isScrollLocked } from 'Utils/dom';
import { EVENT_PLAYER_CHAPTER_CHANGE } from 'Utils/types';
import { isLandscape, isMobile } from 'Utils/viewport';
import createCustomComponents from './custom-video-components';

const context = window.__VUMEDI_VIDEO_DETAIL_CONTEXT__;

const PAS_MAX_RETRIES = 2;
const PAS_RETRY_DELAY_MS = 500;

// `catalog.get` rejects with three shapes: an Error with `statusCode: 0` when the request never
// got a response, the XMLHttpRequest itself (`status`, no `statusCode`) on an HTTP error, and a
// plain `Request Aborted` Error when it is cancelled. The parsed body rides along as `data`.
const describePasError = (error) => {
    const body = Array.isArray(error?.data) ? error.data[0] : null;
    return {
        statusCode: error?.statusCode ?? error?.status ?? 'unknown',
        errorCode: body?.error_code ?? null,
        detail: body?.message || error?.message || error?.statusText || '',
        aborted: error?.message === 'Request Aborted',
        timedOut: error?.code === 'ETIMEDOUT',
    };
};

const isRetryablePasError = ({ statusCode }) => statusCode === 0 || (statusCode >= 500 && statusCode < 600);

export class BrightcovePlayer {
    constructor() {
        this._surface = 'video_page';
        this._lastReportTime = 0;
        this._reportTimeInterval = 20000; // variable, depending on the playback rate
        this._lastPlayerProgressSeconds = -1;
        this._customControls = document.querySelector('.js_videoCustomControls');
        this._seekLabelTimeout = null;
        this._seekControlsTimeout = null;
        this._lastSeekDirection = null;
        this._seekCounter = 0;
        this._currentTime = null;
        this._currentTimeInterval = null;
        // Aborted by dispose() to remove the page-lifetime window/document listeners
        // this wrapper registers (they outlive the Video.js player's own DOM).
        this._disposeController = new AbortController();
        this._isTouchDevice = 'ontouchstart' in window;
        this._isHoveringProgressControl = false;
        this._isHoveringControlBar = false;

        const nextVideoContext = window.__NEXT_VIDEO_CONTEXT__ || {};
        this._countdown = new VideoCountdown({
            nextVideoAPIUrl: nextVideoContext.nextVideoAPIUrl,
            initialTime: 0,
            videoCountdownCompletedUrl: nextVideoContext.videoCountdownCompletedUrl,
        });
        this.isStaticFixedLayout = false;
    }

    async initialize(
        playerElement,
        brightcoveId,
        accountId,
        playerId,
        autoplay = context.autoplay,
        prerollServerUrl = context.prerollServerUrl,
        bcovAuthToken = context?.bcovAuthToken,
    ) {
        /*
            autoplay scenarios:
            direct page visit - autoplay: 'any'; muted: true;
            domain navigation - autoplay: 'any'; muted: false;
            same page refresh - autoplay: 'any'; muted: true;

            Using 'any' instead of boolean true makes Video.js manage the autoplay
            attempt and fire 'autoplay-success' / 'autoplay-failure' events.
            'any' tries unmuted first, then falls back to muted autoplay before
            reporting failure.
        */
        const navigationEntries = performance.getEntriesByType('navigation');
        const pageReloaded = navigationEntries.length > 0 && navigationEntries[0].type === 'reload';
        let muted = pageReloaded || !!autoplay;

        if (!pageReloaded && document.referrer) {
            const referrerUrl = new URL(document.referrer);

            if (referrerUrl.hostname === window.location.hostname) {
                // same domain referrer
                muted = false;
            }
        }

        // R1 app gate is blocking the page — force mute so audio doesn't play under the sheet.
        if (window.__VUMEDI_CONTEXT__?.appGateActive) {
            muted = true;
        }

        // Convert boolean autoplay to Video.js string value for event support.
        // Using 'any' makes Video.js try unmuted first, then fall back to muted
        // autoplay, and fire 'autoplay-success' / 'autoplay-failure' events.
        const autoplayOption = autoplay ? 'any' : false;
        this._autoplayRequested = !!autoplay;
        this._initialMuted = muted;
        this._authorized = !!bcovAuthToken;

        if (!playerId) {
            Sentry.withScope((scope) => {
                scope.setFingerprint(['Brightcove player id missing']);
                Sentry.captureException(
                    new Error(
                        `Brightcove player id missing (account ${accountId}, surface ${this._surface}, ` +
                            `authorized ${this._authorized}) — player cannot load`,
                    ),
                );
            });
        }

        this._player =
            playerElement &&
            (await brightcovePlayerLoader({
                refNode: playerElement,
                refNodeInsert: brightcovePlayerLoader.REF_NODE_INSERT_REPLACE,
                accountId,
                playerId,
                embedId: 'default',
                // With a PAS token we load the video through the catalog (edge-auth) after the
                // player is ready; without one, let player-loader auto-load it by id (policy key).
                ...(bcovAuthToken ? {} : { videoId: brightcoveId }),
                options: {
                    // Videojs options
                    controls: true,
                    preload: 'auto',
                    fluid: true,
                    autoplay: autoplayOption,
                    muted,
                    playsinline: true,
                    controlBar: {
                        remainingTimeDisplay: false,
                    },
                },
            }).then((success) => success.ref));

        this._player.ima3.settings = {
            ...this._player.ima3.settings,
            debug: false,
            serverUrl: prerollServerUrl || '',
            timeout: 5000,
            requestMode: 'onload',
            debugContribAds: false,
            disableCustomPlaybackForIOS10Plus: true,
            showVpaidControls: true,
            vpaidMode: 'DISABLED',
        };

        createCustomComponents(this);

        this._player.on('loadedmetadata', () => {
            // if the `t` query parameter is added, start the video from the value set for the param
            const videoDuration = this._player.duration();
            const queryTimestamp = context.timestamp;

            if (queryTimestamp && queryTimestamp < videoDuration) {
                this._player.currentTime(queryTimestamp);
            }
        });

        this._player.on('loadeddata', () => {
            // move the current timestamp inside the thumbnail preview element
            const timestampElement = document.querySelector('.vjs-time-tooltip');
            const thumbnailElement = document.querySelector('.vjs-thumbnail-display');
            thumbnailElement?.append(timestampElement);
        });

        this._player.on('play', () => {
            const muteButton = document.querySelector('.vjs-mute-control');

            if (this._player.muted()) {
                muteButton.classList.add('vjs-vol-0');
            } else {
                muteButton.classList.remove('vjs-vol-0');
            }
        });

        if (this._player) {
            this._onPlayerReadyHandlers(this._player);
            this._player.controlBar.playbackRateMenuButton.handleClick = () => {}; // disable default behavior
        }

        this._videoControlsHandler();
        this._videoChaptersControls(this._player);
        this._videoLayoutHandler();

        this._currentTimeInterval = setInterval(() => {
            if (!this._player) {
                clearInterval(this._currentTimeInterval);
                return;
            }
            this._currentTime = this._player.currentTime();
        }, 1000);

        if (this._player && bcovAuthToken) {
            this._loadAuthorizedVideo(brightcoveId, bcovAuthToken);
        }

        if (this._player) {
            window.vumedi.mixpanel?.track('VideoPlayerInitialized', {
                video_id: context?.videoId || null,
                video_slug: context?.videoSlug || null,
                channel_id: context?.channelId || null,
                authorized: this._authorized,
                player_id: playerId,
                template_version: context?.templateVersion,
                surface: this._surface,
            });
        }

        // Expose player
        window.player = this._player;
        return this._player;
    }

    /**
     * Tear down the player and every page-lifetime resource this wrapper owns.
     *
     * Video.js `dispose()` removes the player's DOM and its own listeners, but it
     * never touches state held by this wrapper: the 1s `currentTime` interval and
     * the window/document listeners registered against `_disposeController.signal`.
     * Those must be cleared here or each video switch leaks one interval plus a set
     * of `beforeunload`/`scroll`/`keydown` handlers that fire on a disposed player.
     */
    dispose() {
        if (this._currentTimeInterval) {
            clearInterval(this._currentTimeInterval);
            this._currentTimeInterval = null;
        }
        this._disposeController.abort();
        this._player?.dispose();
        this._player = null;
    }

    /**
     * Load the video through the catalog with a Brightcove PAS JWT, so the request goes
     * through authorized delivery (edge-auth) instead of the policy-key path. Used only
     * when the server rendered a `bcovAuthToken`; otherwise player-loader auto-loads by id.
     *
     * Called directly (no `player.ready()` wrapper): player-loader resolves its promise only
     * once the player is loaded, created and ready, so `this._player` is a usable Video.js
     * player here. See https://apis.support.brightcove.com/playback-authorization/guides/using-pas-with-bcp.html
     *
     * `initialize` withholds `videoId` from player-loader when a token is present, so a failure
     * here leaves the player permanently empty — hence the retry and the user-visible error.
     *
     * @param {string} brightcoveId Video Cloud video id
     * @param {string} token Server-minted PAS JWT
     */
    async _loadAuthorizedVideo(brightcoveId, token) {
        for (let attempt = 0; ; attempt++) {
            try {
                const video = await this._player.catalog.get({
                    type: 'video',
                    id: brightcoveId,
                    bcovAuthToken: token,
                });
                // `load` is synchronous today; awaited so a future rejection stays caught here.
                await this._player.catalog.load(video);
                return;
            } catch (error) {
                // Disposed while the request was in flight — nothing left to load into.
                if (!this._player) return;

                const failure = describePasError(error);

                if (attempt < PAS_MAX_RETRIES && isRetryablePasError(failure)) {
                    await new Promise((resolve) => setTimeout(resolve, PAS_RETRY_DELAY_MS));
                    if (!this._player) return;
                    continue;
                }

                this._reportAuthorizedVideoFailure(brightcoveId, error, failure, attempt + 1);
                return;
            }
        }
    }

    /**
     * Report a terminal PAS catalog failure, and tell the user, since the player has no source
     * to fall back to.
     *
     * Every failure is tracked so the rate has one complete denominator. Sentry gets (temporarily) all of them
     * except a status of 0, where the request never reached Brightcove (blocked host, CORS,
     * offline), and aborts, which are just the user navigating away.
     *
     * @param {string} brightcoveId Video Cloud video id
     * @param {Error|object} error Rejection from `catalog.get`
     * @param {object} failure Normalized description from `describePasError`
     * @param {number} attempts How many attempts were made, including the first
     */
    _reportAuthorizedVideoFailure(brightcoveId, error, failure, attempts) {
        const { statusCode, errorCode, detail, aborted, timedOut } = failure;

        window.vumedi.mixpanel?.track('VideoPlaybackAuthFailed', {
            video_id: context?.videoId || null,
            video_slug: context?.videoSlug || null,
            brightcove_id: brightcoveId,
            status_code: statusCode,
            error_code: errorCode,
            aborted,
            timed_out: timedOut,
            attempts,
            surface: this._surface,
            template_version: context?.templateVersion,
        });

        if (!aborted && statusCode !== 0) {
            Sentry.withScope((scope) => {
                scope.setFingerprint(['{{ default }}', String(statusCode), errorCode ?? 'none']);
                scope.setTag('pas_status_code', String(statusCode));
                scope.setTag('pas_error_code', errorCode ?? 'none');
                scope.setTag('brightcove_id', brightcoveId);
                scope.setContext('pas_catalog', {
                    brightcoveId,
                    statusCode,
                    errorCode,
                    detail,
                    attempts,
                    surface: this._surface,
                });
                Sentry.captureException(
                    new Error(
                        `Failed to load authorized video ${brightcoveId} ` +
                            `(status ${statusCode}${errorCode ? ` ${errorCode}` : ''}) after ${attempts} attempts`,
                        { cause: error },
                    ),
                );
            });
        }

        // MEDIA_ERR_NETWORK renders Video.js's built-in error display; without it the player
        // just stays empty.
        this._player?.error({
            code: 2,
            message: 'This video could not be loaded. Please refresh the page to try again.',
        });
    }

    /**
     * Set video player to fixed layout, i.e. user can scroll below the player while the player is "glued" to the upper
     * part of the screen. Same as YouTube on mobile devices.
     *
     * Static mode prevents user from exiting the fixed layout by scrolling up. Used when the video AI assistant is
     * opened on mobile devices. To disable static fixed layout, method `unsetFixedLayout(true)` must be called.
     *
     * @param {boolean} isStatic Whether to enable static fixed layout. Must be disabled with `unsetFixedLayout(true)`.
     */
    setFixedLayout(isStatic = false) {
        if (this.isStaticFixedLayout) {
            return;
        }

        if (isStatic) {
            this.isStaticFixedLayout = true;
        }

        const playerHeight = this._player.el_.getBoundingClientRect().height;

        this._player.el_.classList.add('vjs-fixed-position');
        this._player.el_.parentElement.style.setProperty('height', `${playerHeight}px`);
    }

    unsetFixedLayout(removeStatic = false) {
        if (this.isStaticFixedLayout && !removeStatic) {
            return;
        }

        this.isStaticFixedLayout = false;

        // Only undo a pin this class applied. Callers reach this on players that were never
        // pinned - every scroll and resize outside mobile portrait does - and the height below
        // is the spacer `setFixedLayout()` stamps on the parent, not necessarily one we own.
        if (!this._player.el_.classList.contains('vjs-fixed-position')) {
            return;
        }

        this._player.el_.classList.remove('vjs-fixed-position');
        this._player.el_.parentElement.style.removeProperty('height');
    }

    getPlayerElement() {
        return this._player?.el_;
    }

    /**
     * Sourced from https://stackoverflow.com/a/9039885/2711654
     * Note that `navigator.platform` is deprecated. Its replacement is `navigator.userAgentData`
     * but that's not yet properly supported. That's why the deprecated variant is still used.
     *
     * @returns {boolean|boolean} True if estimated that the client is running on iOS
     */
    _isRunningOnIOS() {
        return (
            ['iPad Simulator', 'iPhone Simulator', 'iPod Simulator', 'iPad', 'iPhone', 'iPod'].includes(
                navigator.platform,
            ) ||
            // iPad on iOS 13 detection
            (navigator.userAgent.includes('Mac') && 'ontouchend' in document)
        );
    }

    _onPlayerReadyHandlers(player) {
        this._handlePlayPermission(player);
        this._handleLandscapeFullscreen();

        if (context?.updateViewedTimeUrl && context.viewedUUID) {
            this._handlePrerollEventsTracking(player);
            this._handlePlayerTimeTracking(player);
            this._handlePlayerFullscreenTracking(player);
            this._handleOrientationTracking();
        }

        // User settings
        player.defaultPlaybackRate(this._loadUserPlaybackRate());
        player.volume(this._loadUserVolumeLevel());
    }

    _setReportingTime(playerProgressSeconds, now, chapterId = undefined) {
        this._lastReportTime = now;
        this._lastPlayerProgressSeconds = Math.max(this._lastPlayerProgressSeconds, playerProgressSeconds);
        if (this.hasChapters && chapterId !== undefined) {
            this._lastPlayerProgressPerChapterSeconds[chapterId] = Math.max(
                this._lastPlayerProgressPerChapterSeconds[chapterId],
                playerProgressSeconds,
            );
        }
    }

    _checkAndReportPlayedTime(playerProgressSeconds, forceUpdate = false, seek = false) {
        const now = new Date().getTime();
        const playerProgressMs = playerProgressSeconds * 1000;

        let chapterId;
        let lastChapterProgressSeconds = Number.MAX_SAFE_INTEGER; // if no chapters then min will pick video progress
        if (this.hasChapters) {
            chapterId = this._findActiveChapterForCurrentTime(playerProgressMs).dataset.chapterId;
            lastChapterProgressSeconds = this._lastPlayerProgressPerChapterSeconds[chapterId];
        }
        const lastPlayerProgressSeconds = Math.min(lastChapterProgressSeconds, this._lastPlayerProgressSeconds);

        if (playerProgressSeconds < lastPlayerProgressSeconds) return;
        if (
            now - this._lastReportTime > this._reportTimeInterval / this._player.playbackRate() ||
            (seek && playerProgressSeconds - lastPlayerProgressSeconds > 10) || // Prevent reporting short seeks
            forceUpdate
        ) {
            this._setReportingTime(playerProgressSeconds, now, chapterId);

            const postJson = JSON.stringify({ viewed_time: playerProgressMs });
            const headers = {
                'X-CSRFToken': Cookies.get('csrftoken') || context.csrftoken,
            };

            // keepalive lets the request finish even if the user navigates away mid-flight,
            // so cancelled-on-unload errors stop reaching Sentry as actionable noise.
            const controller = new AbortController();
            let didTimeout = false;
            const timeoutId = setTimeout(() => {
                didTimeout = true;
                controller.abort();
            }, 10000);

            fetch(context?.updateViewedTimeUrl, {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json; charset=utf-8',
                    ...headers,
                },
                body: postJson,
                keepalive: true,
                credentials: 'same-origin',
                signal: controller.signal,
            })
                .then(async (response) => {
                    if (!response.ok) {
                        const rawResponse = await response.text();
                        let errorDetail;
                        try {
                            const json = JSON.parse(rawResponse);
                            errorDetail = json.detail || JSON.stringify(json);
                        } catch {
                            errorDetail = rawResponse || `Status: ${response.status}`;
                        }
                        throw new Error(`HTTP ${response.status}: ${errorDetail}`);
                    }

                    // eslint-disable-next-line no-console
                    console.log(`Reporting viewed time ${playerProgressMs}`);
                })
                .catch((err) => {
                    if (err.name === 'AbortError') {
                        if (!didTimeout) {
                            // Browser-initiated abort (page unload, keepalive quota, etc.).
                            // Not actionable — drop it.
                            return;
                        }
                        Sentry.withScope((scope) => {
                            scope.setFingerprint(['[TIMEOUT] Set viewed time']);
                            Sentry.captureException(
                                new Error(
                                    `The request has timed out. Data: ${postJson}, Headers: ${JSON.stringify(
                                        headers,
                                    )}, viewed UUID: ${context.viewedUUID}.`,
                                ),
                            );
                        });
                    } else if (err instanceof TypeError) {
                        // Network-layer failure — fetch rejects with a TypeError ("Failed to
                        // fetch" / "Load failed") when the request never reaches the server:
                        // client offline, connection dropped, request cancelled on navigation,
                        // or the page was saved and opened from file://. Equivalent to an XHR
                        // status of 0. Not actionable — drop it. HTTP error responses throw a
                        // plain Error above and still get reported below.
                        return;
                    } else {
                        Sentry.withScope((scope) => {
                            scope.setFingerprint(['Set viewed time error']);
                            Sentry.captureException(
                                new Error(
                                    `Something went wrong with reporting viewed_time. Data: ${postJson}, Headers: ${JSON.stringify(
                                        headers,
                                    )}, viewed UUID: ${context.viewedUUID}, Err: ${err.message || 'Unknown error'}.`,
                                ),
                            );
                        });
                    }
                })
                .finally(() => clearTimeout(timeoutId));
        }
    }

    _getCurrentAdData() {
        const { ima3 } = this._player;

        try {
            for (const value of Object.values(ima3.currentAd)) {
                if ('adWrapperIds' in value) {
                    return value;
                }
            }
            throw new Error('Missing adWrapperIds in values.');
        } catch (err) {
            Sentry.withScope((scope) => {
                scope.setFingerprint(['Preroll ad data error']);
                Sentry.captureException(
                    new Error(
                        `Can not get current ad data from ima3. ima3: ${JSON.stringify(ima3)}, Err: ${JSON.stringify(
                            err,
                        )}.`,
                    ),
                );
            });
        }

        return null;
    }

    _setGoogleAdData() {
        if (!this.lineItemId) {
            const currentAd = this._getCurrentAdData();
            if (currentAd.adWrapperCreativeIds.length) {
                [this.lineItemId] = currentAd.adWrapperIds;
                [this.creativeId] = currentAd.adWrapperCreativeIds;
            } else {
                this.lineItemTitle = currentAd.title;
                this.lineItemId = currentAd.adId;
                this.creativeId = currentAd.creativeId;
            }

            if (Number.isNaN(this.lineItemId % 1) || Number.isNaN(this.creativeId % 1)) {
                Sentry.withScope((scope) => {
                    scope.setFingerprint(['Preroll ad data error']);
                    scope.addAttachment({ filename: 'currentAd.txt', data: JSON.stringify(currentAd) });
                    Sentry.captureException(
                        new Error(`Invalid line item ${this.lineItemId} or creative ${this.creativeId}.`),
                    );
                    scope.clearAttachments();
                });
            }
        }
    }

    _report_perroll_viewed_time(skipped = false, clicked = false, finished = false) {
        const { ad } = this._player.ads;
        let viewedTime = ad.currentTime || 1;

        if (finished) {
            viewedTime = ad.duration;
        }

        this._setGoogleAdData();

        const postData = JSON.stringify({
            viewed_uuid: context.viewedUUID,
            line_item_title: this.lineItemTitle,
            line_item_id: this.lineItemId,
            creative_id: this.creativeId,
            viewed_time: `${viewedTime * 1000}`,
            skipped,
            clicked,
            finished,
        });
        const headers = {
            'X-CSRFToken': Cookies.get('csrftoken') || context.csrftoken,
        };
        $.ajax({
            url: context.prerollAnalyticsUrl,
            dataType: 'json',
            contentType: 'application/json; charset=utf-8',
            data: postData,
            type: 'POST',
            headers,
        })
            .then(() => {
                // eslint-disable-next-line no-console
                console.log(`Reporting preroll viewed time ${viewedTime * 1000}`, skipped, clicked, finished);
            })
            .catch((err) => {
                Sentry.withScope((scope) => {
                    scope.setFingerprint(['Set preroll viewed time error']);
                    Sentry.captureException(
                        new Error(
                            `Something went wrong with reporting preroll viewed_time. Data: ${postData}, Err: ${JSON.stringify(
                                err,
                            )}.`,
                        ),
                    );
                });
            });
    }

    _showSafetyInformation() {
        this._setGoogleAdData();
        $.ajax({
            url: context.videoSafetyInformationUrl,
            data: {
                line_item_id: this.lineItemId,
                creative_id: this.creativeId,
            },
        }).success((data) => {
            $('#video-safety-information-box').html(data);
        });
    }

    _hideSafetyInformation() {
        $('#video-safety-information-box').html('');
    }

    _handlePlayPermission(player) {
        if (!context.playPermission) {
            player.el_.classList.add('vjs-login-required');
            player.on('play', () => {
                player.pause();
                $('#loginOrRegisterModal').modal('show');
            });
        }
    }

    /**
     * Using this.lineItemTitle, this.lineItemId, and this.creativeId properties.
     */
    _handlePrerollEventsTracking(player) {
        // [PY-2386] Viewed time tracking patch for autoplay videos
        player.on('adserror', (e) => {
            if (context.autoplay) {
                e.target.player.trigger('contentresumed');
            }
        });
        player.on('ads-ad-started', () => {
            this._showSafetyInformation();
            this._report_perroll_viewed_time();
        });
        player.on('ads-ad-ended', () => {
            this._report_perroll_viewed_time(false, false, true);
            this._hideSafetyInformation();
        });
        player.on('ads-ad-skipped', () => {
            this._report_perroll_viewed_time(true);
            this._hideSafetyInformation();
        });
        player.on('ads-click', () => {
            this._report_perroll_viewed_time(false, true);
        });

        // Report banner viewed time
        player.on('ads-first-quartile', () => {
            this._report_perroll_viewed_time();
        });
        player.on('ads-midpoint', () => {
            this._report_perroll_viewed_time();
        });
        player.on('ads-third-quartile', () => {
            this._report_perroll_viewed_time();
        });
        window.addEventListener(
            'beforeunload',
            () => {
                if (this.ads) {
                    this._report_perroll_viewed_time();
                }
            },
            { signal: this._disposeController.signal },
        );
    }

    _moengagePlaybackEventAttributes(player) {
        return {
            content_id: Number(context.videoId),
            content_title: context.videoTitle,
            content_format: context.videoFormat,
            viewed_uuid: context.viewedUUID,
            total_duration_seconds: Math.round(player.duration()),
            playback_position_seconds: Math.round(player.currentTime()),
            audio_muted: player.muted(),
        };
    }

    _trackMoengagePlaybackStopped(player, reason) {
        window.vumedi.moengage?.track('ContentPlaybackStopped', {
            ...this._moengagePlaybackEventAttributes(player),
            stopped_reason: reason,
        });
    }

    _handlePlayerTimeTracking(player) {
        this._checkAndReportPlayedTime(0, true); // Sets viewed time to 0

        this._wasPaused = false;

        const onPlaybackStarted = (trigger) => {
            const reportTime = player.currentTime();
            this._checkAndReportPlayedTime(reportTime || 1, true); // Sometimes reportTime can be 0.

            window.vumedi.mixpanel?.track('VideoPlaybackStarted', {
                video_id: context?.videoId || null,
                video_slug: context?.videoSlug || null,
                channel_id: context?.channelId || null,
                trigger,
                muted: player.muted(),
                authorized: this._authorized,
                template_version: context.templateVersion,
                surface: this._surface,
            });
        };

        if (this._autoplayRequested) {
            // When autoplay is requested, Video.js fires a 'play' event even if the
            // browser blocks it (a ghost event). We avoid player.one('play') here and
            // instead use the autoplay-success/failure events to know what really happened.
            player.one('autoplay-success', () => {
                onPlaybackStarted('autoplay');
            });
            player.one('autoplay-failure', () => {
                window.vumedi.mixpanel?.track('VideoAutoplayBlocked', {
                    viewed_uuid: context.viewedUUID,
                    video_id: context?.videoId || null,
                    video_slug: context?.videoSlug || null,
                    channel_id: context?.channelId || null,
                    muted: this._initialMuted,
                    template_version: context.templateVersion,
                    surface: this._surface,
                });
                // Autoplay was blocked. Wait for the user to actually click play.
                player.one('play', () => {
                    onPlaybackStarted('user');
                });
            });
        } else {
            player.one('play', () => {
                onPlaybackStarted('user');
            });
        }
        player.on('ended', (e) => {
            this._checkAndReportPlayedTime(e.target.player.duration(), true);

            window.vumedi.mixpanel?.track('VideoEnded', {
                viewed_uuid: context.viewedUUID,
                video_id: context.videoId,
                video_slug: context.videoSlug || null,
                channel_id: context.channelId || null,
                template_version: context.templateVersion,
                surface: this._surface,
            });

            this._trackMoengagePlaybackStopped(e.target.player, 'natural_completion');

            window.redirectEnabled = true;
            const productWorkshopContext = window.__PRODUCT_WORKSHOP_CONTEXT__;
            // If productWorkshopContext is set countdown will be handled in product_workshop.js file.
            if (productWorkshopContext) {
                if (productWorkshopContext.activityPreTest && !productWorkshopContext.preTestSubmission) {
                    $('#pre-test-modal').modal('show');
                } else if (productWorkshopContext.activityPostTest && !productWorkshopContext.postTestSubmission) {
                    $('#post-test-modal').modal('show');
                } else {
                    // If all tests solved only then run countdown at the end of a video.
                    this._countdown.run();
                }
            } else {
                this._countdown.run();
            }
        });
        player.on('playing', () => {
            this._countdown.hide();
        });
        player.on('pause', () => {
            // Video.js also fires pause at end of video and mid seek, neither is a user pause.
            if (player.ended() || player.seeking()) return;
            this._wasPaused = true;

            window.vumedi.moengage?.track('ContentPlaybackPaused', this._moengagePlaybackEventAttributes(player));
        });
        player.on('play', () => {
            if (!this._wasPaused) return;
            this._wasPaused = false;

            window.vumedi.moengage?.track('ContentPlaybackResumed', this._moengagePlaybackEventAttributes(player));
        });
        player.on('seeked', (e) => {
            const eventPlayer = e.target.player;
            const reportTime = eventPlayer.currentTime();

            this._checkAndReportPlayedTime(reportTime, false, true);

            // sanity check for seek parameter
            if (!reportTime || reportTime <= this._lastPlayerProgressSeconds) return;
            // const seekedAmount = (reportTime - this._lastPlayerProgressSeconds); TODO
            gtag('event', 'seek_forward_time', { event_category: 'Video', event_label: context?.videoId });
        });
        player.on('timeupdate', (e) => {
            // If manuallyTriggered is true that means someone clicked on the scrubber (this is reported in seeked event).
            const manuallyTriggered = !!e.manuallyTriggered;
            if (!manuallyTriggered) {
                const eventPlayer = e.target.player;
                this._checkAndReportPlayedTime(eventPlayer.currentTime());
            }
        });

        window.addEventListener(
            'beforeunload',
            () => {
                const reportTime = player.currentTime();

                if (reportTime) {
                    this._checkAndReportPlayedTime(reportTime, true);
                }
                if (player.hasStarted() && !player.ended()) {
                    this._trackMoengagePlaybackStopped(player, 'navigation');
                }
            },
            { signal: this._disposeController.signal },
        );
    }

    _handlePlayerFullscreenTracking(player) {
        player.on('fullscreenchange', () => {
            if (player.isFullscreen()) {
                window.eventTracker.trackFullscreenOn();
            } else {
                window.eventTracker.trackFullscreenOff();
            }
        });
    }

    /**
     * Turning a phone to landscape is almost always a request for a bigger picture, so enter
     * fullscreen on the rotation itself.
     *
     * Only a crossing into landscape counts, never the standing orientation, so exiting
     * fullscreen while still in landscape is not undone: the user lands on the page, which
     * `_videoLayoutHandler` leaves scrollable because it does not pin the player in landscape.
     */
    _handleLandscapeFullscreen() {
        // Nothing worth enlarging on a locked or preview video.
        if (!context?.playPermission) {
            return;
        }

        // `change` fires when the viewport crosses between orientations and `matches` is the
        // orientation it crossed into. `screen.orientation` cannot be used for this: mid-rotation
        // it reports the orientation being left (`landscape-primary` alongside `angle: 0`), which
        // re-entered the fullscreen the user had just exited on the way back to portrait.
        window.matchMedia('(orientation: landscape)').addEventListener(
            'change',
            (event) => {
                // `isTouchPrimary()` is what tells a phone from a narrow window. `isMobile()`
                // only measures width, so a desktop window dragged into a landscape shape under
                // 992px crosses this media query as well, and without the touch check it would
                // seize the screen of someone who never rotated anything.
                if (!event.matches || !isMobile() || !isTouchPrimary()) {
                    return;
                }

                // Rotating on a video that isn't playing is not a request to watch it, so leave
                // it be rather than throwing the user into fullscreen. `paused()` covers a video
                // that was never started as well as one the user paused.
                if (this._player.paused() || this._player.isFullscreen()) {
                    return;
                }

                // Fullscreen needs user activation, so this does nothing for someone who loads
                // the page and rotates without ever tapping - muted autoplay grants none. One
                // tap anywhere is enough; until then landscape just gives the unpinned,
                // scrollable layout, which is the fallback by design.
                //
                // Both guards are needed - Video.js rejects a promise on the native path and
                // throws synchronously on the iOS one.
                try {
                    Promise.resolve(this._player.requestFullscreen()).catch(() => {});
                } catch {
                    // Fullscreen refused; stay on the scrollable landscape layout.
                }
            },
            { signal: this._disposeController.signal },
        );
    }

    _handleOrientationTracking() {
        // Track only playable videos
        if (!context.playPermission) {
            return;
        }
        const portrait = window.matchMedia('(orientation: portrait)');
        // Track initial orientation
        if (portrait.matches) {
            window.eventTracker.trackOrientationPortrait();
        } else {
            window.eventTracker.trackOrientationLandscape();
        }
        // Track consecutive orientations
        portrait.addEventListener(
            'change',
            (e) => {
                if (e.matches) {
                    window.eventTracker.trackOrientationPortrait();
                } else {
                    window.eventTracker.trackOrientationLandscape();
                }
            },
            { signal: this._disposeController.signal },
        );
    }

    _loadUserPlaybackRate() {
        return UserStore.loadPlaybackRate() || 1;
    }

    _saveUserPlaybackRate(playbackRate) {
        UserStore.savePlaybackRate(playbackRate);
    }

    _loadUserVolumeLevel() {
        const savedVolumeLevel = UserStore.loadVolumeLevel();

        return savedVolumeLevel === undefined ? 1 : savedVolumeLevel;
    }

    _saveUserVolumeLevel(level) {
        UserStore.saveVolumeLevel(level);
    }

    _handleVolumeChange() {
        const volume = this._player.muted() ? 0 : this._player.volume();

        if (volume === 0) {
            this._player.muted(true);
        }

        this._saveUserVolumeLevel(volume);
    }

    _findActiveChapterForCurrentTime(playerProgressMs) {
        return this.chapterItems.find((item) => {
            const { timestampStart, timestampEnd } = item.dataset;
            return playerProgressMs >= timestampStart && playerProgressMs < timestampEnd;
        });
    }

    _handlePlayerRateChange() {
        const currentPlaybackRate = this._player.playbackRate();
        this._saveUserPlaybackRate(currentPlaybackRate);
        this._checkAndReportPlayedTime(this._player.currentTime(), true); // report and reset tracking
        this._trackPlaybackRateChange(currentPlaybackRate);
    }

    _videoChaptersControls(player) {
        // TODO: some of the variables below should probably be class properties.
        this.chaptersContainer = document.querySelector('.js_chaptersContainer');
        this.hasChapters = !!this.chaptersContainer;

        if (!this.hasChapters) return;

        const videoElement = document.querySelector('video-js');
        this.chapterItems = Array.from(this.chaptersContainer.querySelectorAll('.js_chapterItem'));
        this._trackChapterImpression();

        // player progress in here is not relative to chapter
        this._lastPlayerProgressPerChapterSeconds = {};
        for (const chapterItem of this.chapterItems) {
            const { chapterId, timestampStart } = chapterItem.dataset;
            this._lastPlayerProgressPerChapterSeconds[chapterId] = timestampStart / 1000;
        }

        /**
         * Click listener that is called whenever a chapter is changed, caused by following:
         *
         * - Initial playout of the video, i.e. initialization of chapters
         * - Video progress naturally enters into new chapter.
         * - User clicks inside a progress bar somewhere inside non-active chapter.
         * - User clicks on a chapter inside chapter list.
         */
        let chapterViewEndedForVideoEnd = false;
        let isSeeking = false;

        // Wall-clock play time tracking for ChapterViewed duration
        let chapterPlayStartedAt = null;
        let chapterAccumulatedPlayTimeMs = 0;
        let isPlayingInChapter = false;

        const flushPlayTime = () => {
            if (isPlayingInChapter && chapterPlayStartedAt !== null) {
                chapterAccumulatedPlayTimeMs += Date.now() - chapterPlayStartedAt;
                chapterPlayStartedAt = null;
            }
            isPlayingInChapter = false;
        };

        const resetPlayTimeForNewChapter = () => {
            chapterAccumulatedPlayTimeMs = 0;
            chapterPlayStartedAt = null;
            isPlayingInChapter = false;
            if (!player.paused() && !player.ended()) {
                chapterPlayStartedAt = Date.now();
                isPlayingInChapter = true;
            }
        };

        const getCurrentPlayTimeMs = () => {
            let total = chapterAccumulatedPlayTimeMs;
            if (isPlayingInChapter && chapterPlayStartedAt !== null) {
                total += Date.now() - chapterPlayStartedAt;
            }
            return total;
        };

        const emitChapterViewed = (activeItem, transitionTrigger, nextItem = null) => {
            if (!activeItem) return;

            const {
                timestampStart: chapterStart,
                timestampEnd: chapterEnd,
                chapterId: chapterId,
                chapterOrdinal: chapterOrdinal,
                chapterTitle: chapterTitle,
            } = activeItem.dataset;

            if (!chapterStart || !chapterEnd) return;

            const chapterStartMs = parseInt(chapterStart, 10);
            const chapterEndMs = parseInt(chapterEnd, 10);
            const chapterDurationMs = Math.max(chapterEndMs - chapterStartMs, 0);

            flushPlayTime();
            const viewDurationMs = getCurrentPlayTimeMs();

            const newChapterPosition = nextItem?.dataset?.chapterOrdinal
                ? parseInt(nextItem.dataset.chapterOrdinal, 10)
                : null;

            window.vumedi.mixpanel?.track('ChapterViewed', {
                viewed_uuid: context.viewedUUID,
                video_id: context.videoId,
                video_slug: context.videoSlug,
                chapter_id: chapterId,
                chapter_title: chapterTitle,
                current_chapter_position: parseInt(chapterOrdinal, 10),
                new_chapter_position: newChapterPosition,
                total_chapters: this.chapterItems.length,
                chapter_duration_ms: chapterDurationMs,
                view_duration_ms: viewDurationMs,
                transition_trigger: transitionTrigger,
                template_version: context.templateVersion,
            });
        };

        const chapterClickHandler = (item, shouldSkipToChapter = true, transitionTrigger = 'chapter_click') => {
            const { timestampStart, chapterId, chapterOrdinal, chapterTitle } = item.dataset;
            const previousActiveItem = this.chaptersContainer.querySelector('.js_chapterItem.is-active');
            const previousActiveItemId = previousActiveItem?.dataset.chapterId;
            const newActiveItemId = item?.dataset.chapterId;

            if (previousActiveItem && newActiveItemId !== previousActiveItemId) {
                emitChapterViewed(previousActiveItem, transitionTrigger, item);
                resetPlayTimeForNewChapter();
            }

            window.dispatchEvent(new CustomEvent(EVENT_PLAYER_CHAPTER_CHANGE, { detail: { chapterId } }));
            document.querySelector('.vjs-custom-metadata-chapter-title span').innerText = chapterTitle;

            // scroll the user back to the video
            if (window.innerWidth < 768 && shouldSkipToChapter) {
                setTimeout(() => {
                    videoElement.scrollIntoView({ behavior: 'smooth', block: 'start', inline: 'nearest' });
                }, 100);
            }

            previousActiveItem?.classList.remove('is-active');
            item.classList.add('is-active');

            this.chaptersContainer.setAttribute('active-chapter-index', chapterOrdinal - 1);

            if (shouldSkipToChapter) {
                const videoTimestampMiliseconds = Math.floor(this._currentTime * 1000);

                // Make the timeline appear and stay active while the chapter is being changed.
                // The `currentTime` method makes the user inactive, which causes the timeline to disappear.
                // By listening to `userinactive` event and making the user active again,
                // we keep the timeline visible during chapter change.
                const keepActive = () => player.userActive(true);
                player.on('userinactive', keepActive);

                player.userActive(true);

                player.one('seeked', () => {
                    player.off('userinactive', keepActive);
                });

                player.currentTime(timestampStart / 1000);

                // clicking on a chapter will automatically continue video playback if the video is paused
                item.classList.remove('is-paused');

                if (player.paused()) {
                    player.play();
                }

                // Send events only if the clicked chapter is not the already active one
                if (newActiveItemId !== previousActiveItemId) {
                    window.eventTracker.trackChapterClick(
                        videoTimestampMiliseconds,
                        chapterOrdinal,
                        chapterTitle,
                        timestampStart,
                    );
                }
            }
        };

        /**
         * Listener that automatically changes the active chapter based on the video progress.
         *
         * Listener is called when progress naturally enters into new chapter, or when a user clicks
         * inside a progress bar somewhere inside non-active chapter.
         *
         * This listener is not called when a user clicks a chapter inside chapter list.
         */
        const triggerChapterChangeBasedOnCurrentPlayheadTime = (currentTime, transitionTrigger) => {
            const chapterForTimestamp = this._findActiveChapterForCurrentTime(currentTime);
            const activeChapter = this.chaptersContainer.querySelector('.js_chapterItem.is-active');

            if (chapterForTimestamp && chapterForTimestamp !== activeChapter) {
                chapterClickHandler(chapterForTimestamp, false, transitionTrigger);
            }
        };

        player.on('pause', () => {
            flushPlayTime();
            const activeChapter = this.chaptersContainer.querySelector('.js_chapterItem.is-active');
            if (activeChapter) {
                // add class to display "Paused" label
                activeChapter.classList.add('is-paused');
            }
        });

        player.on('play', () => {
            if (!isPlayingInChapter) {
                chapterPlayStartedAt = Date.now();
                isPlayingInChapter = true;
            }
            const activeChapter = this.chaptersContainer.querySelector('.js_chapterItem.is-active');
            // remove class to hide "Paused" label
            activeChapter?.classList.remove('is-paused');
        });

        player.on('seeking', () => {
            flushPlayTime();
            isSeeking = true;
        });

        player.on('seeked', () => {
            const currentTime = player.currentTime() * 1000;
            triggerChapterChangeBasedOnCurrentPlayheadTime(currentTime, 'seek');
            isSeeking = false;
            if (!isPlayingInChapter && !player.paused() && !player.ended()) {
                chapterPlayStartedAt = Date.now();
                isPlayingInChapter = true;
            }
        });

        let lastExecutionTime = 0;
        let thumbnailPreviewMoved = false;

        player.on('timeupdate', () => {
            if (isSeeking) return;

            const currentTime = player.currentTime();
            if (lastExecutionTime === 0 || currentTime - lastExecutionTime >= 10 || lastExecutionTime > currentTime) {
                lastExecutionTime = currentTime;
                triggerChapterChangeBasedOnCurrentPlayheadTime(currentTime * 1000, 'auto_advance');
            }

            if (!thumbnailPreviewMoved) {
                thumbnailPreviewMoved = true;
                const thumbnailPreview = document.querySelector('.vjs-thumbnail-display');
                const thumbnailPreviewParent = thumbnailPreview?.parentNode;
                thumbnailPreviewParent?.insertAdjacentElement('afterend', thumbnailPreview);
            }
        });

        player.one('loadedmetadata', () => {
            const highlightProgressElement = document.createElement('div');
            highlightProgressElement.classList.add('video-chapters__progress-bar', 'js_chapterProgressBar');

            const seekBarElement = player.controlBar.progressControl.seekBar.el();
            seekBarElement.append(highlightProgressElement);

            const progressControlElement = player.controlBar.progressControl.el();

            // Keep player controls visible while hovering over progress control area
            progressControlElement.addEventListener('mouseenter', () => {
                this._isHoveringProgressControl = true;
                player.userActive(true);
            });

            progressControlElement.addEventListener('mouseleave', () => {
                this._isHoveringProgressControl = false;
            });

            progressControlElement.addEventListener('mousemove', () => {
                if (this._isHoveringProgressControl) {
                    player.userActive(true);
                }
            });

            let clipPathString = '0 0, ';

            this.chapterItems.forEach((item, index) => {
                const videoDuration = player.duration() * 1000; // Convert to ms

                const { timestampStart, timestampEnd } = item.dataset;
                const markerStartingPosition = `${(timestampStart / videoDuration) * 100}%`;
                const isFirstChapter = index === 0;

                if (parseInt(markerStartingPosition, 10) > 0) {
                    clipPathString += `${markerStartingPosition} 0%, ${markerStartingPosition} 100%, calc(${markerStartingPosition} + 4px) 100%, calc(${markerStartingPosition} + 4px) 0%, `;
                    if (index + 1 === this.chapterItems.length) {
                        clipPathString += '100% 0%, 100% 100%, 0% 100%';
                    }
                }

                seekBarElement.style.setProperty('--progress-holder-polygon-coordinates', clipPathString);

                item.addEventListener('click', () => chapterClickHandler(item, true, 'chapter_click'));

                const highlightClasses = ['visible', `video-chapters__progress-bar--color-${(index % 5) + 1}`];

                item.addEventListener('mouseenter', () => {
                    const startPercentage = (timestampStart / videoDuration) * 100;
                    const endPercentage = (timestampEnd / videoDuration) * 100;

                    Object.assign(highlightProgressElement.style, {
                        left: `calc(${startPercentage}% + ${isFirstChapter ? 0 : 4}px)`,
                        width: `calc(${endPercentage - startPercentage}% - ${isFirstChapter ? 0 : 4}px)`,
                    });

                    highlightProgressElement.classList.add(...highlightClasses);
                });

                item.addEventListener('mouseleave', () => {
                    highlightProgressElement.classList.remove(...highlightClasses);
                });

                // Create invisible hover zone on progress bar for reverse highlight
                const progressHoverZone = document.createElement('div');
                progressHoverZone.classList.add('video-chapters__progress-hover-zone');
                const startPercentage = (timestampStart / videoDuration) * 100;
                const endPercentage = (timestampEnd / videoDuration) * 100;

                Object.assign(progressHoverZone.style, {
                    left: `${startPercentage}%`,
                    width: `${endPercentage - startPercentage}%`,
                });

                progressHoverZone.addEventListener('mouseenter', () => {
                    item.classList.add('is-hovered');

                    // Scroll to hovered chapter
                    this.chaptersContainer.setAttribute('active-chapter-index', index);

                    Object.assign(highlightProgressElement.style, {
                        left: `calc(${startPercentage}% + ${isFirstChapter ? 0 : 4}px)`,
                        width: `calc(${endPercentage - startPercentage}% - ${isFirstChapter ? 0 : 4}px)`,
                    });

                    highlightProgressElement.classList.add(...highlightClasses);
                });

                progressHoverZone.addEventListener('mouseleave', () => {
                    item.classList.remove('is-hovered');

                    // Restore scroll to active chapter
                    const activeItem = this.chaptersContainer.querySelector('.js_chapterItem.is-active');
                    if (activeItem) {
                        const activeIndex = Array.from(this.chapterItems).indexOf(activeItem);
                        this.chaptersContainer.setAttribute('active-chapter-index', activeIndex);
                    }

                    highlightProgressElement.classList.remove(...highlightClasses);
                });

                progressControlElement.append(progressHoverZone);
            });

            // Set initial chapter
            chapterClickHandler(this.chapterItems[0], false, 'init');
            resetPlayTimeForNewChapter();
        });

        player.on('ended', () => {
            const activeChapter = this.chaptersContainer.querySelector('.js_chapterItem.is-active');
            if (activeChapter) {
                emitChapterViewed(activeChapter, 'video_end');
                chapterViewEndedForVideoEnd = true;
            }
        });

        window.addEventListener(
            'beforeunload',
            () => {
                if (chapterViewEndedForVideoEnd) return;

                const activeChapter = this.chaptersContainer.querySelector('.js_chapterItem.is-active');
                if (activeChapter) {
                    emitChapterViewed(activeChapter, 'page_unload');
                }
            },
            { signal: this._disposeController.signal },
        );
    }

    _trackChapterImpression() {
        const chaptersContainer = this.chaptersContainer;
        if (!chaptersContainer) return;

        const observer = new IntersectionObserver(
            (entries) => {
                entries.forEach((entry) => {
                    if (entry.isIntersecting) {
                        window.vumedi.mixpanel?.track('ChapterImpression', {
                            viewed_uuid: context.viewedUUID,
                            video_id: context.videoId,
                            video_slug: context.videoSlug,
                            channel_id: context?.channelId || null,
                            total_chapters: this.chapterItems.length,
                            template_version: context.templateVersion,
                        });
                        observer.unobserve(chaptersContainer);
                    }
                });
            },
            { threshold: 0.5 },
        );

        observer.observe(chaptersContainer);
    }

    _videoControlsHandler() {
        const fullscreenToggleButton = this._player.getDescendant(['ControlBar', 'FullscreenToggle']);
        const loginRequired = this._player?.el_.classList.contains('vjs-login-required');
        const captionsButton = this._player.controlBar.getChild('SubsCapsButton')?.el_;
        const { tracks_: captionTracks } = this._player.textTracks();
        const activeTrack = captionTracks.find(
            (track) => ['captions', 'subtitles'].includes(track.kind) && track.mode === 'showing',
        );
        let captionsShown = false;

        if (activeTrack) {
            // not sure is it possible or not, but
            // in case there is an active track by default
            // add the active class to the captionsButton
            captionsButton.classList.toggle('is-active');
        }

        const toggleCaptions = () => {
            captionsButton.classList.toggle('is-active');
            captionsShown = !captionsShown;
            captionTracks.forEach((track) => {
                if (['captions', 'subtitles'].includes(track.kind)) {
                    track.mode = captionsShown ? 'showing' : 'disabled'; // Use 'hidden' to hide without disabling
                    window.vumedi.mixpanel?.track('CaptionsToggled', {
                        viewed_uuid: context.viewedUUID,
                        value: captionsShown ? 'showing' : 'disabled',
                        template_version: context.templateVersion,
                        surface: this._surface,
                    });
                }
            });
        };

        captionsButton.addEventListener('click', toggleCaptions);
        captionsButton.addEventListener('touchend', toggleCaptions);

        // blur out the fullscreen button to enable keyboard seeking
        fullscreenToggleButton?.on('click', () => {
            fullscreenToggleButton.blur();
        });

        document.addEventListener(
            'keydown',
            (event) => {
                if (loginRequired) return;

                if (event.key === 'ArrowRight') {
                    this._seekVideo('forward', 'key');
                } else if (event.key === 'ArrowLeft') {
                    this._seekVideo('backward', 'key');
                }
            },
            { signal: this._disposeController.signal },
        );

        this._player.controlBar?.progressControl?.on('mousedown', () => {
            this._seekbarHandler();
        });

        this._player.controlBar?.progressControl?.seekBar?.on('mousedown', () => {
            this._seekbarHandler();
        });

        // Keep controls visible while hovering over control bar
        const controlBarElement = this._player.controlBar?.el();
        if (controlBarElement) {
            controlBarElement.addEventListener('mouseenter', () => {
                this._isHoveringControlBar = true;
                this._player.userActive(true);
            });

            controlBarElement.addEventListener('mouseleave', () => {
                this._isHoveringControlBar = false;
            });

            // Also keep active when clicking on control bar
            controlBarElement.addEventListener('click', () => {
                this._player.userActive(true);
            });
        }

        // Prevent controls from hiding while user is hovering over control bar
        this._player.on('userinactive', () => {
            if (this._isHoveringControlBar || this._isHoveringProgressControl) {
                this._player.userActive(true);
            }
        });

        this._player.on('ratechange', () => {
            this._handlePlayerRateChange();
        });
    }

    _videoLayoutHandler() {
        // Document-space top of the player, memoised so scrolling does not re-measure it.
        // Measured from the wrapper rather than the player: the wrapper stays in the flow, so
        // its top is still correct while the player itself is pinned and out of flow.
        let scrollOffset = null;

        const updateFixedLayout = () => {
            // A landscape viewport is shorter than the player, so pinning covers the whole
            // screen - the user can reach neither the player's own controls nor the page below
            // it. Leave the player in the flow and let the page scroll normally instead.
            //
            // Deliberately ahead of the scroll-lock check below: rotating with a bottom sheet or
            // modal open must still unpin, and this branch never reads `scrollY` so the lock is
            // irrelevant to it. Note this is still a no-op while the video AI assistant holds the
            // static fixed layout, which owns the pin until it closes.
            if (!isMobile() || isLandscape()) {
                scrollOffset = null;
                this.unsetFixedLayout();
                return;
            }

            // `lockScroll()` parks `body` at `position: fixed`, which zeroes `scrollY` and would
            // make the comparison below unpin the player behind the overlay.
            if (isScrollLocked()) return;

            if (scrollOffset === null) {
                const wrapper = this._player.el_.parentElement;
                scrollOffset = Math.floor(wrapper.getBoundingClientRect().top + window.scrollY);
            }

            if (window.scrollY > scrollOffset) {
                this.setFixedLayout();
            } else {
                this.unsetFixedLayout();
            }
        };

        window.addEventListener('scroll', updateFixedLayout, { signal: this._disposeController.signal });

        // Covers both a rotation and a window crossing the mobile breakpoint, either of which
        // invalidates the memoised offset.
        window.addEventListener(
            'resize',
            () => {
                scrollOffset = null;
                updateFixedLayout();
            },
            { signal: this._disposeController.signal },
        );
    }

    _trackPlaybackRateChange(playbackRate) {
        const eventTimestampMiliseconds = Math.floor(this._currentTime * 1000);
        window.eventTracker.trackPlaybackRateChange(playbackRate, eventTimestampMiliseconds);
        gtag('event', 'playback_rate_changed', {
            event_category: 'Video',
            event_label: context?.videoId,
            playback_rate: playbackRate,
            video_timestamp: eventTimestampMiliseconds,
        });
        window.vumedi.mixpanel?.track('PlaybackRateChanged', {
            viewed_uuid: context.viewedUUID,
            playback_rate: playbackRate,
            video_timestamp: eventTimestampMiliseconds,
            template_version: context.templateVersion,
            surface: this._surface,
        });
    }

    _seekbarHandler() {
        const seekedTime = this._player.currentTime();
        const direction = seekedTime > this._currentTime ? 'forward' : 'backward';
        this._seekVideo(direction, 'scrubber');
    }

    _startControlsTimeout(eventName, customControlsWrap) {
        // the seek controls will dissapear after 3 seconds unless another action (seek forward/backward) is triggered
        clearTimeout(this._seekControlsTimeout);
        this._seekControlsTimeout = setTimeout(() => {
            // Don't hide controls if user is hovering over control bar or progress control
            if (this._isHoveringControlBar || this._isHoveringProgressControl) {
                return;
            }
            if (['mouseover', 'touchend', 'click'].includes(eventName)) {
                customControlsWrap?.classList.add('disable-hover-styles');
            }
            customControlsWrap?.classList.remove('show-seek-controls');
            setTimeout(() => {
                this._player.userActive(false);
            }, 1);
        }, 3000);
    }

    _seekVideo(direction, source) {
        const eventTimestampMiliseconds = Math.floor(this._currentTime * 1000);
        const currentTime = this._player.currentTime();
        const duration = Math.floor(this._player.duration());
        const remainingTime = duration - this._currentTime;
        const directionTitleCase = direction.charAt(0).toUpperCase() + direction.slice(1);
        const sourceTitleCase = source.charAt(0).toUpperCase() + source.slice(1);

        window.vumedi.mixpanel?.track('VideoSeekTriggered', {
            viewed_uuid: context.viewedUUID,
            video_timestamp: eventTimestampMiliseconds,
            direction: directionTitleCase,
            source: sourceTitleCase,
            template_version: context.templateVersion,
            surface: this._surface,
        });

        gtag('event', 'video_seek_triggered', {
            event_category: 'Video',
            event_label: context?.videoId,
            video_timestamp: eventTimestampMiliseconds,
            direction: directionTitleCase,
            source: sourceTitleCase,
        });

        window.eventTracker.trackVideoSeek(sourceTitleCase, directionTitleCase, eventTimestampMiliseconds);

        if (!['button', 'key'].includes(source)) return;

        this._startControlsTimeout();

        if (direction === 'forward') {
            if (remainingTime >= 10) {
                this._player.currentTime(currentTime + 10);
                this._showSeekLabel('forward');
            } else {
                this._player.currentTime(duration);
            }
        } else if (currentTime >= 10) {
            this._player.currentTime(currentTime - 10);
            this._showSeekLabel('backward');
        } else {
            this._player.currentTime(0);
        }
    }

    _showSeekLabel(direction) {
        const seekLabel = document.querySelector('.js_videoSeekLabel');
        clearTimeout(this._seekLabelTimeout);

        if (direction !== this._lastSeekDirection) {
            this._seekCounter = 0;
        }

        this._lastSeekDirection = direction;
        seekLabel?.classList.remove('playing-forward', 'playing-backward');
        this._seekCounter += direction === 'forward' ? 1 : -1;
        seekLabel?.classList.add(`playing-${direction}`, 'is-visible');

        const labelParagraph = seekLabel?.querySelector('p');
        if (labelParagraph) {
            labelParagraph.innerText = `${Math.abs(this._seekCounter * 10)} seconds`;
        }

        this._seekLabelTimeout = setTimeout(() => {
            seekLabel?.classList.remove('is-visible');
            this._seekCounter = 0;
            this._lastSeekDirection = null;
        }, 2000);
    }
}

export class EmbeddedBrightcovePlayer extends BrightcovePlayer {
    _onPlayerReadyHandlers() {}

    /**
     * Pinning the player to the top of an embed iframe is meaningless - the iframe is the whole
     * viewport - and `setFixedLayout()` would overwrite the inline `height` the embed template
     * puts on the player's parent, collapsing the player once unpinned.
     */
    _videoLayoutHandler() {}
}

/**
 * Brightcove player with view-time tracking but without page-specific
 * features (countdown, orientation, fullscreen, preroll tracking).
 * Used in the Askmedi detail panel where the full video page DOM is absent.
 */
export class TrackingBrightcovePlayer extends BrightcovePlayer {
    _onPlayerReadyHandlers(player) {
        if (context?.updateViewedTimeUrl && context.viewedUUID) {
            this._handlePlayerTimeTracking(player);
        }

        player.defaultPlaybackRate(this._loadUserPlaybackRate());
        player.volume(this._loadUserVolumeLevel());
    }

    /**
     * Pins the player to the top of the window while scrolling, which is wrong for a player
     * sitting inside an overlay panel - and is one of the page-level features named above.
     */
    _videoLayoutHandler() {}
}

export class AskmediBrightcovePlayer extends TrackingBrightcovePlayer {
    constructor() {
        super(...arguments);
        this._surface = 'askmedi';
    }
}
